import { useContext, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useNavigate, useLocation } from "react-router-dom";
import { empty, prepareResponseData } from "../../Utilities/utils";
import { AuthContext } from "../Root/ProtectedRoute";

// css
import "../students/Students.css";

// api
import staffApi from "../../api/Staff";

//components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import { Toast } from "primereact/toast";
import SchoolStaffTable from "../../components/tables/primeTable/staff/SchoolStaffTable";
import TableLoading from "../../components/skeleton/TableLoading";

const SchoolStaff = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  //ref
  const toastTR = useRef(null);
  // states
  const [staffData, setStaffData] = useState([]);
  const [staffToToggleDialog, setStaffToToggleDialog] = useState(false);
  const [selectedStaffDetail, setSelectedStaffDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // fetch staff
    try {
      getStaff();
    } catch (error) {
      responseDialog(
        "error",
        "Something went wrong",
        "Failed to load staff. Please try again later."
      );
      if (!empty(location) && !empty(location.state)) {
        navigate(null, {
          replace: true,
          state: { staffAdded: false, staffUpdated: false },
        });
      }
    }
  }, []);

  //alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  // function to get all staff
  const getStaff = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const schoolId =
        !empty(user) && !empty(user.schoolId) ? user.schoolId : "";
      const response = await staffApi.getStaff(schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return setStaffData([]);
      } else {
        setStaffData(response_data.response);
      }
    } catch (error) {
      responseDialog("error", "Error Alert", `Something went wrong.`);
    } finally {
      setIsLoading(false);
    }
  };

  //function to toggle staff visibility
  const toggleStaffVisiblity = async () => {
    try {
      if (staffToToggleDialog) setStaffToToggleDialog(false);
      if (!isLoading) setIsLoading(true);
      const schoolId =
        !empty(user) && !empty(user.schoolId) ? user.schoolId : "";
      const active =
        !empty(selectedStaffDetail) && !empty(selectedStaffDetail.active)
          ? selectedStaffDetail.active
          : false;
      const staffId =
        !empty(selectedStaffDetail) && !empty(selectedStaffDetail._id)
          ? selectedStaffDetail._id
          : "";
      const response = await staffApi.toggleStaffVisibility(
        staffId,
        active,
        schoolId
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        const error_response =
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : `Failed to ${
                !empty(selectedStaffDetail) &&
                !empty(selectedStaffDetail.active) &&
                selectedStaffDetail.active === false
                  ? "enable"
                  : "disable"
              } staff with name, ${
                !empty(selectedStaffDetail.lastName) &&
                !empty(selectedStaffDetail.firstName)
                  ? `${selectedStaffDetail.lastName} ${selectedStaffDetail.firstName}`
                  : ""
              }`;
        return responseDialog("error", "Something went wrong", error_response);
      }

      responseDialog(
        "success",
        "Success",
        `Staff, ${
          !empty(selectedStaffDetail.lastName) &&
          !empty(selectedStaffDetail.firstName)
            ? `${selectedStaffDetail.lastName} ${selectedStaffDetail.firstName}`
            : ""
        }, was ${
          !empty(selectedStaffDetail) &&
          !empty(selectedStaffDetail.active) &&
          selectedStaffDetail.active === true
            ? "disabled"
            : "enabled"
        } successfully!`
      );
      return getStaff();
    } catch (error) {
      return responseDialog(
        "error",
        "Something went wrong",
        "Request failed please try again later"
      );
    } finally {
      setIsLoading(false);
    }
  };

  //function to hide confirm modal
  const hideDisableStaffDialog = () => {
    setStaffToToggleDialog(false);
  };

  const disableStaffDialogFooter = (
    <div
      style={{
        marginTop: 20,
      }}
    >
      <Button
        label="No"
        icon="pi pi-times"
        style={{
          backgroundColor: "transparent",
          color: "#e65061",
          borderColor: "#e65061",
          borderWidth: 1,
        }}
        onClick={hideDisableStaffDialog}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        style={{
          backgroundColor: "transparent",
          color: "#389d17",
          borderColor: "#389d17",
          borderWidth: 1,
        }}
        onClick={toggleStaffVisiblity}
      />
    </div>
  );

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="tableCard">
            {/*  header start */}
            <MainHeader title="Staff" />
            {/* end of header */}

            {/* table start */}
            {!isLoading ? (
              <SchoolStaffTable
                confirmDisableSelected
                goToUpdateStaffScreen
                schoolStaff={staffData}
              />
            ) : (
              <TableLoading />
            )}
            {/* table end  */}
          </div>
        </main>
      </AppWrapper>

      <Dialog
        visible={staffToToggleDialog}
        style={{ width: "32rem", zIndex: 999999 }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={disableStaffDialogFooter}
        onHide={hideDisableStaffDialog}
      >
        <div
          className="confirmation-content"
          style={{ marginTop: 20, display: "flex", alignItems: "center" }}
        >
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem", marginRight: 15, color: "#e65061" }}
          />
          {
            <span>
              Are you sure you want to{" "}
              {!empty(selectedStaffDetail) &&
              !empty(selectedStaffDetail.active) &&
              selectedStaffDetail.active ? (
                <span style={{ color: "red" }}>
                  <strong>disable</strong>
                </span>
              ) : (
                <span style={{ color: "green" }}>
                  <strong>enable</strong>
                </span>
              )}{" "}
              the selected staff -{" "}
              <strong>
                {!empty(selectedStaffDetail) &&
                !empty(selectedStaffDetail.title)
                  ? selectedStaffDetail.title
                  : ""}
              </strong>
              {selectedStaffDetail && (
                <b>
                  {!empty(selectedStaffDetail.name)
                    ? selectedStaffDetail.name
                    : ""}
                </b>
              )}
              ?
            </span>
          }
        </div>
      </Dialog>
      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />
    </>
  );
};

export default SchoolStaff;
